/* Anania v7.3 PROD — consolidated storefront runtime. */

/* Anania production scripts — countdown (single shared ticker, hidden-tab aware) */
(() => {
  'use strict';

  const pad = (n) => String(n).padStart(2, '0');
  let countdowns = [];
  let ticker = 0;

  const refreshCountdowns = () => {
    countdowns = Array.from(document.querySelectorAll('[data-aq-countdown="true"]'));
  };

  const updateOne = (section, now) => {
    const output = section.querySelector('[data-aq-countdown-output]');
    if (!output) return;
    const hour = Number(section.dataset.aqHour || 0);
    const minute = Number(section.dataset.aqMinute || 0);
    const target = new Date(now);
    target.setHours(hour, minute, 0, 0);
    if (target <= now) target.setDate(target.getDate() + 1);
    const total = Math.max(0, Math.floor((target - now) / 1000));
    const next = `${pad(Math.floor(total / 3600))}:${pad(Math.floor((total % 3600) / 60))}:${pad(total % 60)}`;
    if (output.textContent !== next) output.textContent = next;
  };

  const tick = () => {
    if (!countdowns.length || document.hidden) return;
    const now = new Date();
    countdowns.forEach((section) => updateOne(section, now));
  };

  const stop = () => {
    if (!ticker) return;
    window.clearInterval(ticker);
    ticker = 0;
  };

  const start = () => {
    refreshCountdowns();
    if (!countdowns.length || document.hidden) {
      stop();
      return;
    }
    tick();
    if (!ticker) ticker = window.setInterval(tick, 1000);
  };

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) stop();
    else start();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', start);
  document.addEventListener('shopify:section:unload', start);
})();

/* Anania desktop mega navigation — hover, keyboard and editor safe. */
(() => {
  'use strict';

  const desktop = window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');
  const selector = '[data-aq-mega-menu]';
  const openTimers = new WeakMap();
  const closeTimers = new WeakMap();

  const allMenus = () => Array.from(document.querySelectorAll(selector));
  const clearTimer = (map, details) => {
    const id = map.get(details);
    if (id) window.clearTimeout(id);
    map.delete(details);
  };

  const syncBody = () => {
    document.body?.classList.toggle('aq-mega-open', desktop.matches && allMenus().some((menu) => menu.open));
  };

  const closeMenu = (details) => {
    clearTimer(openTimers, details);
    clearTimer(closeTimers, details);
    details.removeAttribute('open');
    details.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'false');
    syncBody();
  };

  const closeAll = (except = null) => {
    allMenus().forEach((details) => {
      if (details !== except && details.open) closeMenu(details);
    });
    syncBody();
  };

  const openMenu = (details) => {
    if (!desktop.matches) return;
    clearTimer(closeTimers, details);
    closeAll(details);
    details.setAttribute('open', '');
    details.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'true');
    syncBody();
  };

  const scheduleOpen = (details) => {
    clearTimer(closeTimers, details);
    if (details.open) return;
    clearTimer(openTimers, details);
    openTimers.set(details, window.setTimeout(() => openMenu(details), 75));
  };

  const scheduleClose = (details) => {
    clearTimer(openTimers, details);
    clearTimer(closeTimers, details);
    closeTimers.set(details, window.setTimeout(() => closeMenu(details), 150));
  };

  const bindMenu = (details) => {
    if (details.dataset.aqMegaBound === 'true') return;
    details.dataset.aqMegaBound = 'true';
    const host = details.closest('header-menu') || details;
    const summary = details.querySelector(':scope > summary');

    host.addEventListener('pointerenter', () => scheduleOpen(details));
    host.addEventListener('pointerleave', () => scheduleClose(details));
    host.addEventListener('focusin', () => clearTimer(closeTimers, details));
    host.addEventListener('focusout', () => {
      window.setTimeout(() => {
        if (!host.contains(document.activeElement)) closeMenu(details);
      }, 0);
    });

    summary?.addEventListener('click', () => {
      window.setTimeout(() => {
        if (details.open) closeAll(details);
        syncBody();
      }, 0);
    });

    details.addEventListener('toggle', syncBody);
  };

  const init = (scope = document) => {
    scope.querySelectorAll?.(selector).forEach(bindMenu);
    syncBody();
  };

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const open = allMenus().find((menu) => menu.open);
    if (!open) return;
    const summary = open.querySelector(':scope > summary');
    closeAll();
    summary?.focus();
  });

  document.addEventListener('pointerdown', (event) => {
    if (!desktop.matches || !document.body.classList.contains('aq-mega-open')) return;
    if (event.target.closest?.('.header-wrapper')) return;
    closeAll();
  }, { passive: true });

  desktop.addEventListener?.('change', () => {
    if (!desktop.matches) closeAll();
    else syncBody();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();

/* Anania's Shop v7.3 PROD — low-cost floating header state controller.
   The previous implementation continuously morphed width/radius/height during
   the first ~120px of scrolling. Backdrop-filter + layout writes in that exact
   range caused visible jank. This version only writes when the header crosses
   stable enter/leave thresholds, with hysteresis to prevent threshold chatter. */
(() => {
  'use strict';

  const root = document.documentElement;
  const coarsePointer = window.matchMedia('(pointer: coarse)');
  let headerSection = null;
  let ownsStickyState = false;
  let scrolledState = null;
  let lastViewportBucket = '';
  let scrollFrame = 0;
  let resizeFrame = 0;
  let lastViewportWidth = window.innerWidth;

  const ENTER_SCROLL_Y = 72;
  const LEAVE_SCROLL_Y = 24;

  const refreshHeaderTargets = () => {
    const sticky = document.querySelector('sticky-header');
    headerSection = document.querySelector('.section-header');
    const type = sticky?.getAttribute('data-sticky-type');
    ownsStickyState = type === 'always' || type === 'reduce-logo-size';
  };

  const viewportBucket = () => {
    const width = window.innerWidth;
    return width >= 990 ? 'desktop' : width >= 750 ? 'tablet' : 'mobile';
  };

  const headerMetrics = (scrolled) => {
    const bucket = viewportBucket();
    if (bucket === 'desktop') {
      return scrolled
        ? { inset: 8, radius: 3.45, minHeight: 6.35, pad: .48 }
        : { inset: 4, radius: 2.2, minHeight: 7.2, pad: .7 };
    }
    if (bucket === 'tablet') {
      return scrolled
        ? { inset: 2.3, radius: 2.4, minHeight: 6.15, pad: .45 }
        : { inset: 1.4, radius: 1.75, minHeight: 6.5, pad: .55 };
    }
    return scrolled
      ? { inset: 1.45, radius: 2, minHeight: 5.8, pad: .39 }
      : { inset: 1, radius: 1.55, minHeight: 6, pad: .45 };
  };

  const applyHeaderState = (scrolled, force = false) => {
    const bucket = viewportBucket();
    if (!force && scrolledState === scrolled && lastViewportBucket === bucket) return;

    scrolledState = scrolled;
    lastViewportBucket = bucket;
    const metrics = headerMetrics(scrolled);

    root.style.setProperty('--v727-header-inset', `${metrics.inset}rem`);
    root.style.setProperty('--v727-header-radius', `${metrics.radius}rem`);
    root.style.setProperty('--v727-header-min-height', `${metrics.minHeight}rem`);
    root.style.setProperty('--v727-header-pad-y', `${metrics.pad}rem`);
    root.classList.toggle('v72-scrolled', scrolled);

    if (ownsStickyState) headerSection?.classList.toggle('scrolled-past-header', scrolled);
  };

  const resolveStateFromScroll = (force = false) => {
    const y = Math.max(0, window.scrollY || window.pageYOffset || 0);
    let next = scrolledState;

    if (next === null) next = y >= ENTER_SCROLL_Y;
    else if (!next && y >= ENTER_SCROLL_Y) next = true;
    else if (next && y <= LEAVE_SCROLL_Y) next = false;

    applyHeaderState(Boolean(next), force);
  };

  const queueHeaderUpdate = () => {
    if (scrollFrame || document.hidden) return;

    // Deep-page scrolling cannot change the state, so avoid scheduling work.
    const y = window.scrollY || window.pageYOffset || 0;
    if (scrolledState === true && y > LEAVE_SCROLL_Y) return;
    if (scrolledState === false && y < ENTER_SCROLL_Y) return;

    scrollFrame = requestAnimationFrame(() => {
      scrollFrame = 0;
      resolveStateFromScroll(false);
    });
  };

  const normalizeExternalCards = (scope = document) => {
    scope.querySelectorAll?.('.card-wrapper').forEach((card) => {
      if (card.closest('.product-grid, .aq-scroll-rail, .related-products')) {
        card.classList.add('aq-product-card');
      }
    });
  };

  const markTouchModality = () => {
    root.classList.toggle('v72-touch', coarsePointer.matches);
  };

  const reflowHeader = () => {
    refreshHeaderTargets();
    markTouchModality();
    resolveStateFromScroll(true);
  };

  const queueHeaderReflow = () => {
    if (resizeFrame || document.hidden) return;
    resizeFrame = requestAnimationFrame(() => {
      resizeFrame = 0;
      const width = window.innerWidth;
      // Mobile browser chrome frequently fires resize events while scrolling even
      // though the layout width did not change. Avoid forced header rewrites for
      // those height-only resizes; orientation/real breakpoint changes still reflow.
      if (Math.abs(width - lastViewportWidth) < 4) return;
      lastViewportWidth = width;
      reflowHeader();
    });
  };

  const init = (scope = document) => {
    refreshHeaderTargets();
    normalizeExternalCards(scope);
    reflowHeader();
    root.classList.add('v72-ready');
  };

  window.addEventListener('scroll', queueHeaderUpdate, { passive: true });
  window.addEventListener('resize', queueHeaderReflow, { passive: true });
  window.addEventListener('pageshow', reflowHeader, { passive: true });
  coarsePointer.addEventListener?.('change', reflowHeader);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();

/* Anania Shop v7.2.6 PROD — safe persistent Black / White mode. */
(() => {
  'use strict';
  const root = document.documentElement;
  const KEY = 'anania:color-mode';

  const read = () => {
    if (document.body?.classList.contains('anania-bf')) return 'dark';
    const current = root.dataset.v72Theme;
    if (current === 'dark' || current === 'light') return current;
    try {
      const saved = localStorage.getItem(KEY);
      if (saved === 'dark' || saved === 'light') return saved;
    } catch (e) {}
    return 'light';
  };

  const sync = (mode) => {
    const dark = mode === 'dark';
    document.querySelectorAll('[data-v72-theme-toggle]').forEach((button) => {
      const label = dark ? (button.dataset.v72LabelLight || 'Enable light mode') : (button.dataset.v72LabelDark || 'Enable dark mode');
      button.setAttribute('aria-pressed', String(dark));
      button.setAttribute('aria-label', label);
      button.setAttribute('title', label);
      const hidden = button.querySelector('[data-v72-theme-toggle-label]');
      if (hidden) hidden.textContent = label;
    });
    const meta = document.getElementById('AnaniaThemeColor');
    if (meta) meta.setAttribute('content', dark ? '#000000' : '#f5f5f7');
    document.querySelectorAll('[data-anania-mode-logo]').forEach((image) => {
      const next = dark ? image.dataset.logoDark : image.dataset.logoLight;
      if (next && image.getAttribute('src') !== next) image.setAttribute('src', next);
    });
  };

  const apply = (mode, persist = true) => {
    if (document.body?.classList.contains('anania-bf')) { mode = 'dark'; persist = false; }
    mode = mode === 'dark' ? 'dark' : 'light';
    root.dataset.v72Theme = mode;
    root.style.colorScheme = mode;
    root.classList.toggle('v72-dark', mode === 'dark');
    sync(mode);
    if (persist) {
      try { localStorage.setItem(KEY, mode); } catch (e) {}
    }
    document.dispatchEvent(new CustomEvent('anania:themechange', { detail: { mode } }));
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest?.('[data-v72-theme-toggle]');
    if (!button) return;
    event.preventDefault();
    apply(read() === 'dark' ? 'light' : 'dark');
  });

  document.addEventListener('shopify:section:load', () => sync(read()));
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => apply(read(), false), { once: true });
  } else {
    apply(read(), false);
  }
})();
